import clsx, { ClassValue } from "clsx";
import { Image } from "@/components/common";
import { useElement } from "@/hooks/common/use-element";
import { PlainObject, RequireOnlyOne } from "@/interfaces/common";
import { isElement } from "@/utils";

// Define StaticImageData type locally since we're not using Next.js
type StaticImageData = {
  src: string;
  height: number;
  width: number;
  blurDataURL?: string;
};

import React from "react";
import { Icon, IconName } from "../Icon";

export type AlertVariant = "success" | "error" | "warning" | "neutral";

type Props = RequireOnlyOne<
  {
    className?: ClassValue;
    variant: AlertVariant;
    icon?: IconName | React.ReactElement | null;
    image?: StaticImageData | string;
    size?: number;
    mediaClassName?: ClassValue;
  },
  "icon" | "image"
>;

type Variant = "success" | "error" | "warning" | "neutral";

type Classes = {
  default: string;
  variable: {
    [Key in Variant]: string;
  };
  icon: {
    [Key in Variant]: IconName;
  };
  iconColor: {
    [Key in Variant]: string;
  };
  media: string;
  iconBase: string;
};

export const AlertIcon = ({ className, variant, icon, image, size = 56, mediaClassName }: Props): JSX.Element => {
  const classes: Classes = {
    default: "flex items-center justify-center rounded-full",
    variable: {
      success: "bg-success text-white",
      error: "bg-error-background",
      warning: "bg-warning",
      neutral: "[--alert-color:var(--neutral)]",
    },
    icon: {
      success: "Check",
      error: "Close",
      warning: "Error",
      neutral: "Info",
    },
    iconColor: {
      success: "text-white",
      error: "text-error",
      warning: "text-white",
      neutral: "text-neutral",
    },
    media: "h-full min-h-8 w-full min-w-8 flex items-center justify-center grow",
    iconBase: "flex items-center justify-center",
  };

  const margin = size / 2.75;
  const iconToUse = icon !== undefined && icon !== null ? icon : classes.icon[variant];

  const [IconElement] = useElement<PlainObject>(
    {
      size: `${size * (Math.PI / 90)}rem`,
      className: clsx(classes.iconBase, mediaClassName),
    },
    isElement(iconToUse) ? iconToUse : <Icon name={iconToUse} className={clsx(classes.iconColor[variant])} />,
  );

  return (
    <div className="relative z-10">
      <div
        className={clsx(classes.default, classes.variable[variant], className)}
        style={{
          width: size,
          height: size,
          margin: `${margin}px`,
        }}
      >
        {image ? (
          <div className={clsx(classes.media, mediaClassName)}>
            <Image src={typeof image === "string" ? image : image.src} alt="Alerta" priority className="w-full" />
          </div>
        ) : (
          IconElement && <IconElement />
        )}
      </div>
    </div>
  );
};
